import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquareText, FileText, Megaphone, Upload, Pencil, Phone, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

const ICONS = { reply: MessageSquareText, note: StickyNote, import: Upload, edit: Pencil, call: Phone, campaign: Megaphone };

export function ActivityCard({ buyerId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const load = () => API.get(`/buyers/${buyerId}/activity`).then(r => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [buyerId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!text.trim()) return;
    await API.post(`/buyers/${buyerId}/notes`, { text }); setText(''); toast.success('Note added'); load();
  };
  const logReply = async () => {
    if (!text.trim()) return;
    const r = await API.post('/buyer-replies', { buyer_id: buyerId, channel: 'sms', text });
    setText(''); toast.success(r.data.action === 'opted_out' ? 'STOP received — contact opted out of SMS' : 'Reply logged'); load();
  };
  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="activity-card">
      <CardContent className="p-5 space-y-3">
        <p className="text-base font-semibold text-gray-900 flex items-center gap-2"><MessageSquareText className="h-4 w-4 text-[#0073ea]" /> Notes & Activity</p>
        <div className="flex gap-2">
          <Textarea data-testid="note-input" value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Add a note…" className="bg-white border-gray-300 text-sm" />
          <div className="flex flex-col gap-1 self-end">
            <Button data-testid="add-note-btn" onClick={add} className="bg-[#0073ea] hover:bg-[#0060c2] text-white h-8">Add note</Button>
            <Button data-testid="log-reply-btn" onClick={logReply} variant="outline" className="h-8">Log reply</Button>
          </div>
        </div>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-gray-400 text-center py-2">No activity yet</p>}
          {items.map(a => {
            const Icon = ICONS[a.type] || Pencil;
            return (
              <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-2.5 text-sm">
                <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><Icon className="h-3.5 w-3.5 text-gray-500" /></div>
                <div><p className="text-gray-800 whitespace-pre-wrap">{a.text}</p><p className="text-[11px] text-gray-400">{new Date(a.created_at).toLocaleString()} · {a.type}</p></div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function MatchedLeadsCard({ buyerId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { API.get(`/buyers/${buyerId}/matched-leads`).then(r => setItems(r.data)).catch(() => {}); }, [buyerId]);
  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="matched-leads-card">
      <CardContent className="p-5 space-y-2">
        <p className="text-base font-semibold text-gray-900 flex items-center gap-2"><FileText className="h-4 w-4 text-[#0073ea]" /> Matched Leads</p>
        {items.length === 0 ? <p className="text-sm text-gray-400 text-center py-2">No matching leads yet</p> : items.slice(0, 8).map(l => (
          <button key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`matched-lead-${l.id}`} className="w-full flex items-center justify-between p-2.5 rounded-lg border border-gray-200 hover:border-[#0073ea]/40 text-left">
            <span className="text-sm text-gray-900 truncate">{l.property_address || l.name}</span><Badge variant="outline" className="text-[10px]">{l.status}</Badge>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

export function BuyerCampaignsCard({ buyerId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { API.get(`/buyers/${buyerId}/campaigns`).then(r => setItems(r.data)).catch(() => {}); }, [buyerId]);
  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="buyer-campaigns-card">
      <CardContent className="p-5 space-y-2">
        <p className="text-base font-semibold text-gray-900 flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#0073ea]" /> Campaign History</p>
        {items.length === 0 ? <p className="text-sm text-gray-400 text-center py-2">Not in any campaign yet</p> : items.map((c, i) => (
          <div key={`${c.id}-${i}`} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-gray-100">
            <Badge variant="outline" className="text-[10px]">{c.channel}</Badge>
            <span className="flex-1 truncate text-gray-800">{c.subject || c.message}</span>
            <span className="text-[11px] text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span>
            <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
